import { useState } from "react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Users, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { getInitials, getAvatarColor } from "@/lib/avatar"
import { getUserDisplayName } from "@/lib/user-display"
import { useAuthStore } from "@/stores/auth-store"

interface UserPickerProps {
  value: string[]
  onChange: (ids: string[]) => void
  users: { id: string; email: string; fullName?: string | null | undefined; avatarUrl?: string | null | undefined }[]
}

function UserPicker({ value, onChange, users }: UserPickerProps) {
  const [search, setSearch] = useState("")
  const [open, setOpen] = useState(false)
  const currentUserId = useAuthStore((s) => s.user?.id ?? null)

  const filteredUsers = users.filter((user) => {
    if (!search) return true
    const query = search.toLowerCase()
    const nameMatch = user.fullName?.toLowerCase().includes(query)
    const emailMatch = user.email.toLowerCase().includes(query)
    return nameMatch || emailMatch
  })

  const handleToggle = (userId: string) => {
    const isSelected = value.includes(userId)
    if (isSelected) {
      onChange(value.filter((id) => id !== userId))
    } else {
      onChange([...value, userId])
    }
  }

  const triggerLabel = value.length > 0
    ? `${value.length} assigned`
    : "Assign users"

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Users className="h-4 w-4" />
          <span>{triggerLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-xs"
            />
          </div>
        </div>
        <div className="max-h-48 overflow-y-auto p-1">
          {filteredUsers.length === 0 && (
            <div className="px-3 py-4 text-center text-meta">
              No users found
            </div>
          )}
          {filteredUsers.map((user) => {
            const isSelected = value.includes(user.id)
            const displayName = getUserDisplayName(
              { userId: user.id, fullName: user.fullName, email: user.email },
              currentUserId
            )
            const hasAvatar = !!user.avatarUrl

            return (
              <button
                key={user.id}
                type="button"
                onClick={() => handleToggle(user.id)}
                className={cn(
                  "flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-left",
                  "hover:bg-muted transition-colors",
                  isSelected && "bg-muted/50"
                )}
              >
                <Checkbox
                  checked={isSelected}
                  className="pointer-events-none"
                  tabIndex={-1}
                />
                {(() => {
                  const color = getAvatarColor(user.id)
                  return (
                    <div className={cn("h-6 w-6 rounded-full flex items-center justify-center shrink-0 overflow-hidden", hasAvatar ? "bg-muted" : color.bg)}>
                      {hasAvatar ? (
                        <img
                          src={user.avatarUrl!}
                          alt={displayName}
                          className="h-6 w-6 rounded-full object-cover"
                        />
                      ) : (
                        <span className={cn("text-[10px] font-medium", color.text)}>
                          {getInitials(user.fullName)}
                        </span>
                      )}
                    </div>
                  )
                })()}
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{displayName}</div>
                  {user.fullName && (
                    <div className="text-meta truncate">
                      {user.email}
                    </div>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { UserPicker }
export type { UserPickerProps }
